import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowDropDown,
  ArrowDropUp,
  Menu,
  MenuOpen
} from '@mui/icons-material';
import {
  Box,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SxProps,
  Theme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';

import CurrentUser from '@/components/CurrentUser';
import FacilitySelector from '@/components/FacilitySelector';
import { EXACARE_LOGO_URLS } from '@/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsLandscapeMobileViewport } from '@/hooks/useIsLandscapeMobileViewport';
import { usePylonChat } from '@/hooks/usePylonChat';
import { useSidebarExpandedAtom } from '@/hooks/useSidebarExpandedAtom';
import InboxModal from '@/pages/AdminHub/Staff/Notifications/components/InboxModal';

import { SidebarItem, useGetSidebarCRMItems } from './getSidebarCRMItems';
import HelpCenterButton from './HelpCenterButton';
import { SIDEBAR_COLORS } from './sidebarTheme';

const COLLAPSED_BUTTON_SIZE = 48;
const EXPANDED_BUTTON_SIZE = 56;
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 64;

export const StyledNavButton = styled(ListItemButton)({
  minHeight: COLLAPSED_BUTTON_SIZE,
  height: COLLAPSED_BUTTON_SIZE,
  borderRadius: '100px',
  '&.Mui-disabled': {
    opacity: 1
  },
  '&.Mui-selected': {
    '&:hover': {
      background: '#D3E5F4'
    },
    background: '#D3E5F4'
  }
});

export const StyledNavButtonOutlined = styled(StyledNavButton)({
  border: '1px solid #D4DEE7',
  '& .MuiListItemAvatar-root': {
    minWidth: 'unset'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 'unset',
    '& .MuiSvgIcon-root': {
      color: '#364955'
    }
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 500,
    color: SIDEBAR_COLORS.textPrimary
  },
  '& .MuiListItemText-secondary': {
    fontSize: '12px!important',
    color: SIDEBAR_COLORS.textSecondary
  },
  '& .MuiTypography-root': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

export const getCollapsibleButtonSx = (
  expanded: boolean,
  variant: 'default' | 'avatar' = 'default'
): SxProps<Theme> => ({
  width: expanded ? '100%' : `${COLLAPSED_BUTTON_SIZE}px !important`,
  height: `${COLLAPSED_BUTTON_SIZE}px`,
  minWidth: expanded ? undefined : `${COLLAPSED_BUTTON_SIZE}px !important`,
  maxWidth: expanded ? undefined : `${COLLAPSED_BUTTON_SIZE}px !important`,
  justifyContent: expanded ? 'flex-start' : 'center',
  padding: expanded
    ? variant === 'avatar'
      ? '6px 16px 6px 8px'
      : '6px 16px'
    : '0 !important',
  gap: expanded ? '8px' : undefined
});

export const collapsibleItemSx: SxProps<Theme> = {
  minWidth: 'unset',
  margin: 0,
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export const getCollapsibleIconSx = (
  expanded: boolean,
  expandedMarginRight = '12px'
): SxProps<Theme> => ({
  minWidth: 'unset',
  marginRight: expanded ? expandedMarginRight : '0 !important'
});

export const getNavButtonSx = (expanded: boolean): SxProps<Theme> => ({
  width: expanded ? '100%' : `${COLLAPSED_BUTTON_SIZE}px !important`,
  minWidth: expanded ? undefined : `${COLLAPSED_BUTTON_SIZE}px !important`,
  maxWidth: expanded ? undefined : `${COLLAPSED_BUTTON_SIZE}px !important`,
  justifyContent: expanded ? 'flex-start' : 'center',
  padding: expanded ? undefined : '0 !important',
  height: expanded ? `${EXPANDED_BUTTON_SIZE}px` : `${COLLAPSED_BUTTON_SIZE}px`,
  minHeight: expanded
    ? `${EXPANDED_BUTTON_SIZE}px`
    : `${COLLAPSED_BUTTON_SIZE}px`,
  borderRadius: expanded ? '100px' : '50%'
});

interface DropdownArrowProps {
  isOpen: boolean;
}

export const DropdownArrow: React.FC<DropdownArrowProps> = ({ isOpen }) =>
  isOpen ? <ArrowDropUp /> : <ArrowDropDown />;

interface SidebarListItemProps {
  expanded: boolean;
  children: React.ReactNode;
}

const SidebarListItem: React.FC<SidebarListItemProps> = ({
  expanded,
  children
}) => (
  <ListItem
    disablePadding
    sx={{
      display: 'flex',
      justifyContent: expanded ? 'flex-start' : 'center',
      width: '100%'
    }}
  >
    {children}
  </ListItem>
);

interface InboxModalWrapperProps {
  userId: string;
  collapsed?: boolean;
}

const InboxModalWrapper: React.FC<InboxModalWrapperProps> = ({
  userId,
  collapsed
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: collapsed ? '10px' : 0,
      marginTop: collapsed ? 0 : '-4px',
      '& svg': { fontSize: '20px' },
      '& p': {
        top: collapsed ? '-8px !important' : '-6px !important',
        right: collapsed ? '-12px !important' : '-12px !important'
      }
    }}
  >
    <InboxModal userId={userId} />
  </Box>
);

export const isCurrentPath = (item: SidebarItem, pathname: string) => {
  const isDomainPath =
    pathname.startsWith('/crm') || pathname.startsWith('/ehr');
  const pathbase = (
    pathname.split('/').filter(Boolean)[isDomainPath ? 1 : 0] ?? pathname
  ).toLowerCase();
  const pathtext = (
    item.text.split(' ').filter(Boolean)[0] ?? ''
  ).toLowerCase();

  if (pathname === item.path) {
    return true;
  }

  const itemPathSegments = item.path.split('/').filter(Boolean);
  const pathnameSegments = pathname.split('/').filter(Boolean);

  // Match based on segments, ensuring the pathbase is exactly matched
  // as a whole segment and not just a substring
  if (
    itemPathSegments.length >= 2 &&
    pathnameSegments.length >= 2 &&
    itemPathSegments[0] === pathnameSegments[0] &&
    itemPathSegments[1] === pathbase
  ) {
    return true;
  }

  if (isDomainPath && pathnameSegments.length <= 2) {
    return pathbase === pathtext;
  }

  return false;
};

const Sidebar = () => {
  const currentUser = useCurrentUser().data!;
  const { unreadMessageCount, toggleChat } = usePylonChat();
  const isLandscapeMobile = useIsLandscapeMobileViewport();
  const pathname = useLocation().pathname;
  const crmSidebarItems = useGetSidebarCRMItems();

  const isMediaQueryDownLg = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('lg')
  );
  const isMediaQueryDownMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const [expanded, setExpanded] = useSidebarExpandedAtom();

  React.useEffect(() => {
    setExpanded(!(isMediaQueryDownLg || isMediaQueryDownMd));
  }, [isMediaQueryDownLg, isMediaQueryDownMd, setExpanded]);

  return (
    <Stack
      component="nav"
      sx={{
        width: expanded
          ? `${SIDEBAR_WIDTH_EXPANDED}px`
          : `${SIDEBAR_WIDTH_COLLAPSED}px`,
        transition: 'width 200ms ease-in-out'
      }}
      direction="column"
    >
      <List dense disablePadding>
        <Stack
          padding="16px 16px 6px"
          width="100%"
          direction={isMediaQueryDownMd || !expanded ? 'column' : 'row'}
          alignItems="center"
          justifyContent={expanded ? 'flex-start' : 'center'}
          gap={expanded ? 1 : 2}
        >
          {isMediaQueryDownMd ? (
            <CardMedia
              component="img"
              src={EXACARE_LOGO_URLS.heart}
              sx={{ width: 30, height: 30, objectFit: 'contain' }}
            />
          ) : (
            <IconButton
              sx={{ padding: 0 }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <MenuOpen /> : <Menu />}
            </IconButton>
          )}
          {expanded && (
            <Box sx={{ flex: 1 }}>
              <CardMedia component="img" src={EXACARE_LOGO_URLS.horizontal} />
            </Box>
          )}
          {!isLandscapeMobile && expanded && (
            <InboxModalWrapper userId={currentUser.id} />
          )}
          {!isLandscapeMobile && !expanded && (
            <InboxModalWrapper userId={currentUser.id} collapsed />
          )}
        </Stack>
      </List>
      <Stack
        overflow="auto"
        sx={{
          flex: 1,
          minHeight: 0,
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        <List dense disablePadding sx={{ px: expanded ? 1 : 0 }}>
          {crmSidebarItems.map((item, index) => {
            const { icon, text, path, onClick } = item;
            return (
              <SidebarListItem expanded={expanded} key={index}>
                <StyledNavButton
                  // @ts-ignore
                  component={onClick ? 'button' : Link}
                  to={onClick ? undefined : path}
                  onClick={onClick}
                  selected={isCurrentPath(item, pathname)}
                  sx={getNavButtonSx(expanded)}
                >
                  <ListItemIcon
                    sx={{
                      ...getCollapsibleIconSx(expanded, '4px'),
                      '& svg': { fontSize: '20px' }
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  {expanded && (
                    <ListItemText
                      sx={{ paddingLeft: 1 }}
                      primaryTypographyProps={{
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        fontWeight: isCurrentPath(item, pathname) ? 700 : 500,
                        color: SIDEBAR_COLORS.textPrimary
                      }}
                      primary={text}
                    />
                  )}
                </StyledNavButton>
              </SidebarListItem>
            );
          })}
        </List>
      </Stack>
      {/* Bottom buttons - outside scrollable area to always stick to bottom */}
      <List
        dense
        disablePadding
        sx={{
          px: 1,
          py: { xs: 1, sm: 1.5, md: 2 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '4px', sm: '6px', md: '8px' }
        }}
      >
        <SidebarListItem expanded={expanded}>
          <HelpCenterButton
            expanded={expanded}
            unreadMessageCount={unreadMessageCount}
            onToggleChat={toggleChat}
          />
        </SidebarListItem>
        <SidebarListItem expanded={expanded}>
          <FacilitySelector {...{ expanded }} />
        </SidebarListItem>
        <SidebarListItem expanded={expanded}>
          <CurrentUser {...{ expanded }} />
        </SidebarListItem>
      </List>
    </Stack>
  );
};

export default Sidebar;
